import React, { useEffect, useMemo, useState } from 'react';
import emendaService, { type EmendaDTO } from '../services/emendaService';
import { getErrorMessage } from '../services/api';
import institutionService, { type InstitutionDTO } from '../services/institutionService';
import planoService from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import { STATUS_OSC_LABELS, type StatusOSC } from '../types/statusOSC.types';
import { emendaEtapa } from '../utils/emendaEtapa';
import EmendaProcessoBoard from '../components/emendas/EmendaProcessoBoard';
import DashboardPagination from '../components/DashboardPagination';
import EmendasFilterBar from '../components/EmendasFilterBar';
import { useEmendasFilter } from '../hooks/useEmendasFilter';
import { useEmendaRoleFlags } from '../hooks/useEmendaRoleFlags';
import { UI_THEME } from '../ui/theme';

const normalizeStatus = (value?: string) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const SEM_PLANO = '__SEM_PLANO__';

const PLANO_ETAPA_LABELS: Record<string, string> = {
  ENVIO: 'Envio / assinatura',
  ORCAMENTO: 'Or\u00e7amento',
  CONVENIOS_ANALISE: 'Conv\u00eanios \u2014 an\u00e1lise',
  GESTOR_PARCERIA: 'Gestor da parceria',
  COMISSAO_SELECAO: 'Comiss\u00e3o de sele\u00e7\u00e3o',
  CONVENIOS_MINUTA: 'Conv\u00eanios \u2014 minuta',
  PROCURADORIA_JURIDICA: 'Procuradoria Jur\u00eddica (PGM)',
  GESTOR_JURIDICO_REVISAO: 'Revis\u00e3o do Gestor Jur\u00eddico',
  CONVENIOS_FORMALIZACAO: 'Conv\u00eanios \u2014 formaliza\u00e7\u00e3o',
  CONCLUIDO: 'Conclu\u00eddo',
};

const EmendasVisaoGeralPage: React.FC = () => {
  const { hasRole, user, isAdmin, isGestor, isVereador, canFilterByVereador } = useEmendaRoleFlags();
  const isSaas = UI_THEME === 'saas';

  const surfaceClass = 'border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900/80';
  const heroClass = isSaas
    ? 'border-teal-100 bg-gradient-to-br from-white via-teal-50/70 to-indigo-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800'
    : 'border-slate-200 bg-gradient-to-br from-white via-slate-50 to-sky-50 p-6 shadow-sm dark:border-slate-700 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800';
  const accentTextClass = isSaas ? 'text-teal-700 dark:text-teal-300' : 'text-sky-700 dark:text-sky-300';
  const labelTextClass = 'text-slate-500 dark:text-slate-400';
  const bodyTextClass = 'text-slate-600 dark:text-slate-300';
  const strongTextClass = 'text-slate-900 dark:text-slate-100';

  const [emendas, setEmendas] = useState<EmendaDTO[]>([]);
  const [institutions, setInstitutions] = useState<InstitutionDTO[]>([]);
  const [planos, setPlanos] = useState<PlanoTrabalho[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filtros dedicados a cada coluna do board.
  // A etapa da emenda reutiliza o etapaFilter do hook (fonte única, também usado
  // pela barra de filtros — que oculta esse controle nesta tela via hideEtapa).
  const [instituicaoStatusFilter, setInstituicaoStatusFilter] = useState('');
  const [planoEtapaFilter, setPlanoEtapaFilter] = useState('');
  const [transferenciaFilter, setTransferenciaFilter] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [emendaRows, institutionRows, planoRows] = await Promise.all([
          emendaService.listWithDetails(),
          institutionService.listForUser().catch(() => [] as InstitutionDTO[]),
          // minhaFila=false: o board precisa de TODOS os planos para juntar por emenda,
          // não apenas os da fila do perfil atual.
          planoService.listAll({ minhaFila: false }).catch(() => [] as PlanoTrabalho[]),
        ]);
        setEmendas(Array.isArray(emendaRows) ? emendaRows : []);
        setInstitutions(Array.isArray(institutionRows) ? institutionRows : []);
        setPlanos(Array.isArray(planoRows) ? planoRows : []);
      } catch (err: any) {
        setError(getErrorMessage(err, 'Erro ao carregar emendas.'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // Índices para juntar emenda → instituição → plano de trabalho.
  const institutionsById = useMemo(() => {
    const map = new Map<string, InstitutionDTO>();
    for (const inst of institutions) {
      if (inst.institutionId) map.set(inst.institutionId, inst);
      if (inst.id) map.set(inst.id, inst);
    }
    return map;
  }, [institutions]);

  const planosByEmendaId = useMemo(() => {
    const map = new Map<string, PlanoTrabalho>();
    for (const plano of planos) {
      if (!plano.emendaId) continue;
      const key = String(plano.emendaId);
      const atual = map.get(key);
      // Mantém o plano mais recentemente atualizado quando há mais de um por emenda.
      if (!atual) { map.set(key, plano); continue; }
      const t = new Date(plano.dataAtualizacao || plano.dataCriacao || 0).getTime();
      const tAtual = new Date(atual.dataAtualizacao || atual.dataCriacao || 0).getTime();
      if (t >= tAtual) map.set(key, plano);
    }
    return map;
  }, [planos]);

  const {
    query, setQuery,
    statusFilter, setStatusFilter,
    etapaFilter, setEtapaFilter,
    year, setYear,
    esferaFilter, setEsferaFilter,
    detailFilter, setDetailFilter,
    vereador, setVereador,
    secretaria, setSecretaria,
    myQueueOnly, setMyQueueOnly,
    filtered,
    myQueueCount,
    myQueueStatuses,
    isQueueRestricted,
    clearFilters,
  } = useEmendasFilter(emendas, { hasRole, user, isAdmin, isGestor });

  const vereadores = useMemo(() => {
    const names = new Set<string>();
    emendas.forEach((e) => { if (e.councilorName) names.add(e.councilorName); });
    return Array.from(names).sort();
  }, [emendas]);

  const secretarias = useMemo(() => {
    const names = new Set<string>();
    emendas.forEach((e) => { if (e.secretariaDestino) names.add(e.secretariaDestino); });
    return Array.from(names).sort();
  }, [emendas]);

  const anos = useMemo(() => {
    const years = new Set<string>();
    emendas.forEach((e) => {
      const y = e.exercicio ? String(e.exercicio) : (e.date || '').slice(0, 4);
      if (y) years.add(y);
    });
    return Array.from(years).sort().reverse();
  }, [emendas]);

  // ── Opções dos filtros por coluna (derivadas dos dados presentes) ────────────
  const emendaEtapaOptions = useMemo(() => {
    const set = new Set<string>();
    emendas.forEach((e) => { const l = emendaEtapa(e).label; if (l) set.add(l); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [emendas]);

  const instituicaoStatusOptions = useMemo(() => {
    const set = new Set<string>();
    institutions.forEach((i) => { if (i.statusOSC) set.add(i.statusOSC); });
    return Array.from(set).sort((a, b) =>
      (STATUS_OSC_LABELS[a as StatusOSC] || a).localeCompare(STATUS_OSC_LABELS[b as StatusOSC] || b, 'pt-BR'));
  }, [institutions]);

  const planoEtapaOptions = useMemo(() => {
    const set = new Set<string>();
    planos.forEach((p) => { if (p.etapaAtual) set.add(p.etapaAtual); });
    return Array.from(set).sort((a, b) =>
      (PLANO_ETAPA_LABELS[a] || a).localeCompare(PLANO_ETAPA_LABELS[b] || b, 'pt-BR'));
  }, [planos]);

  // ── Linhas exibidas: emenda-level (hook, inclui etapa da emenda) + filtros de
  //    instituição (status) e plano (etapa) ────────────────────────────────────
  const boardEmendas = useMemo(() => {
    return filtered.filter((emenda) => {
      // Direta = tudo que não é Indireta (inclui não informado), como no restante do app.
      if (transferenciaFilter === 'Indireta' && emenda.tipoTransferencia !== 'Indireta') return false;
      if (transferenciaFilter === 'Direta' && emenda.tipoTransferencia === 'Indireta') return false;

      const inst = emenda.institutionId ? institutionsById.get(emenda.institutionId) : undefined;
      if (instituicaoStatusFilter && (inst?.statusOSC || '') !== instituicaoStatusFilter) return false;

      const plano = emenda.id ? planosByEmendaId.get(String(emenda.id)) : undefined;
      if (planoEtapaFilter) {
        if (planoEtapaFilter === SEM_PLANO) { if (plano) return false; }
        else if ((plano?.etapaAtual || '') !== planoEtapaFilter) return false;
      }
      return true;
    });
  }, [filtered, institutionsById, planosByEmendaId, transferenciaFilter, instituicaoStatusFilter, planoEtapaFilter]);

  const clearAllFilters = () => {
    clearFilters();
    setInstituicaoStatusFilter('');
    setPlanoEtapaFilter('');
    setTransferenciaFilter('');
  };

  // ── Paginação (21 por página — fecha 7 linhas de 3 cards) ────────────────────
  const PAGE_SIZE = 21;
  const [page, setPage] = useState(1);
  useEffect(() => {
    setPage(1);
  }, [query, statusFilter, etapaFilter, year, esferaFilter, detailFilter, vereador, secretaria, myQueueOnly, transferenciaFilter, instituicaoStatusFilter, planoEtapaFilter]);
  const totalPages = Math.max(1, Math.ceil(boardEmendas.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedEmendas = boardEmendas.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const selectClass =
    'w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 outline-none transition focus:border-slate-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100';

  const summaryCards = [
    { label: 'Total exibido', value: boardEmendas.length, dot: 'bg-slate-400' },
    {
      label: 'Pendentes',
      value: boardEmendas.filter((e) => ['recebido', 'iniciado', 'em execucao'].includes(normalizeStatus(e.status || 'Recebido'))).length,
      dot: 'bg-amber-500',
    },
    {
      label: 'Concluidas',
      value: boardEmendas.filter((e) => normalizeStatus(e.status) === 'concluido').length,
      dot: isSaas ? 'bg-teal-500' : 'bg-emerald-500',
    },
    { label: 'Devolvidas', value: boardEmendas.filter((e) => e.status === 'Devolvido').length, dot: 'bg-red-500' },
  ];

  return (
    <div className="w-full space-y-6">
      <div className={`rounded-3xl border ${heroClass}`}>
        <div className="space-y-2">
          <p className={`text-xs font-semibold uppercase tracking-[0.2em] ${accentTextClass}`}>
            {isSaas ? 'Workspace' : 'Gestao'}
          </p>
          <h1 className={`text-2xl font-bold tracking-tight sm:text-3xl ${strongTextClass}`}>
            {isSaas ? 'Quadro operacional de emendas' : 'Visao Geral de Emendas'}
          </h1>
          <p className={`mt-2 max-w-3xl text-sm leading-6 ${bodyTextClass}`}>
            {isSaas
              ? 'Acompanhe cada processo em três frentes — emenda, instituição e plano de trabalho — com status e responsável de cada etapa.'
              : 'Cada linha mostra o processo em três colunas — emenda, instituição e plano de trabalho — com o status e o responsável de cada uma.'}
          </p>
        </div>

        <div className={`mt-5 overflow-hidden rounded-2xl border ${surfaceClass}`}>
          <div className="grid divide-y divide-slate-200 sm:grid-cols-2 sm:divide-x sm:divide-y-0 xl:grid-cols-4 dark:divide-slate-700">
            {summaryCards.map((card) => (
              <div key={card.label} className="min-h-[72px] px-4 py-3">
                <p className={`flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${labelTextClass}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${card.dot}`} />
                  {card.label}
                </p>
                <p className={`mt-2 text-2xl font-bold ${strongTextClass}`}>{card.value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className={`rounded-3xl border p-5 ${surfaceClass}`}>
        <EmendasFilterBar
          query={query}
          onQueryChange={setQuery}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          etapaFilter={etapaFilter}
          onEtapaFilterChange={setEtapaFilter}
          detailFilter={detailFilter}
          onDetailFilterChange={setDetailFilter}
          year={year}
          onYearChange={setYear}
          anos={anos}
          vereador={vereador}
          onVereadorChange={setVereador}
          vereadores={vereadores}
          canFilterByVereador={canFilterByVereador}
          secretaria={secretaria}
          onSecretariaChange={setSecretaria}
          secretarias={secretarias}
          canFilterBySecretaria={canFilterByVereador}
          myQueueOnly={myQueueOnly}
          onMyQueueToggle={() => setMyQueueOnly((v) => !v)}
          myQueueCount={myQueueCount}
          myQueueStatuses={myQueueStatuses}
          isQueueRestricted={isQueueRestricted}
          onClearFilters={clearAllFilters}
          variant="dashboard"
          hideEtapa
          transferencia={transferenciaFilter}
          onTransferenciaChange={setTransferenciaFilter}
          esfera={esferaFilter}
          onEsferaChange={setEsferaFilter}
        />
      </div>

      {/* Filtros dedicados a cada coluna do board */}
      <div className={`rounded-3xl border p-5 ${surfaceClass}`}>
        <div className="grid gap-3 md:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-sky-700 dark:text-sky-300">
              Emenda — etapa
            </label>
            <select
              className={selectClass}
              value={etapaFilter === 'Todas' ? '' : etapaFilter}
              onChange={(e) => setEtapaFilter(e.target.value || 'Todas')}
            >
              <option value="">Todas as etapas</option>
              {emendaEtapaOptions.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-teal-700 dark:text-teal-300">
              Instituição — status
            </label>
            <select className={selectClass} value={instituicaoStatusFilter} onChange={(e) => setInstituicaoStatusFilter(e.target.value)}>
              <option value="">Todos os status</option>
              {instituicaoStatusOptions.map((s) => (
                <option key={s} value={s}>{STATUS_OSC_LABELS[s as StatusOSC] || s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-violet-700 dark:text-violet-300">
              Plano de trabalho — etapa
            </label>
            <select className={selectClass} value={planoEtapaFilter} onChange={(e) => setPlanoEtapaFilter(e.target.value)}>
              <option value="">Todas as etapas</option>
              <option value={SEM_PLANO}>Sem plano de trabalho</option>
              {planoEtapaOptions.map((s) => (
                <option key={s} value={s}>{PLANO_ETAPA_LABELS[s] || s}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </div>
      )}

      {loading && (
        <div className={`flex items-center justify-center py-16 text-sm ${labelTextClass}`}>
          <svg className={`mr-2 h-5 w-5 animate-spin ${isSaas ? 'text-teal-600 dark:text-teal-300' : 'text-sky-600 dark:text-sky-300'}`} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          Carregando emendas...
        </div>
      )}

      {!loading && !error && (
        <>
          <EmendaProcessoBoard
            emendas={pagedEmendas}
            institutionsById={institutionsById}
            planosByEmendaId={planosByEmendaId}
          />
          {boardEmendas.length > PAGE_SIZE && (
            <DashboardPagination
              page={currentPage}
              pageSize={PAGE_SIZE}
              totalItems={boardEmendas.length}
              onPageChange={setPage}
              itemLabel="emendas"
            />
          )}
        </>
      )}
    </div>
  );
};

export default EmendasVisaoGeralPage;
